/**
 * Staff training program — shared types and API helpers (TRUS-486, Amigos Program 1).
 *
 * One module for every call the training surfaces make, so the shape of a training or a
 * completion record is declared once. The backend is the source of truth for these
 * shapes; where a field can be genuinely unknown (is_mandatory, due_date) the type says
 * `null` rather than defaulting, because "not yet decided" and "no" are different facts
 * throughout this feature.
 */

import { getWeaveinApiUrl } from '@/src/config'
import { authFetch, authFetchJson } from '@/lib/authFetch'

const BASE = '/api/workforce'

export const TRAINING_TYPES = [
  'onboarding',
  'otj',
  'compliance',
  'career_development',
  'safety',
  'technology',
  'clinical',
  'program',
  'leadership',
  'wellness',
  'other',
] as const
export type TrainingType = (typeof TRAINING_TYPES)[number]

export const MODALITIES = ['in_person', 'video', 'self_paced'] as const
export type Modality = (typeof MODALITIES)[number]

export const UPDATE_PERMISSIONS = ['admin_only', 'admin_upload', 'staff_self_report'] as const
export type UpdatePermission = (typeof UPDATE_PERMISSIONS)[number]

/** Human labels, kept beside the wire values so no screen invents its own wording. */
export const TRAINING_TYPE_LABELS: Record<TrainingType, string> = {
  onboarding: 'Onboarding',
  otj: 'On-the-job',
  compliance: 'Compliance',
  career_development: 'Career development',
  safety: 'Safety',
  technology: 'Technology skills',
  clinical: 'Clinical / program',
  program: 'Program',
  leadership: 'Leadership',
  wellness: 'Wellness',
  other: 'Other',
}

export const MODALITY_LABELS: Record<Modality, string> = {
  // TRUS-549 ask 1. A LABEL change: the stored value stays `in_person`, so no data
  // migration and no risk to the live catalogue. On-the-job training is not added
  // here — it is already the `otj` training TYPE, and a second spelling of it as a
  // modality would give the same idea two homes.
  in_person: 'Instruction Led',
  video: 'Video',
  self_paced: 'Self-paced',
}

export const UPDATE_PERMISSION_LABELS: Record<UpdatePermission, string> = {
  admin_only: 'Admin only',
  admin_upload: 'Upload by admin',
  staff_self_report: 'Staff can self-report',
}

export interface Competency {
  id: number
  seq: number | null
  name: string
  definition: string | null
  is_active?: boolean
}

export interface Training {
  id: number
  cbo_id: number
  title: string
  description: string | null
  training_type: TrainingType
  modality: Modality | null
  hours: number | null
  /** null = the client has not decided. Never render this as "No". */
  is_mandatory: boolean | null
  /** Completion is only fully evidenced once a certificate issued OUTSIDE
   *  the platform is attached to the record. Does not block recording. */
  requires_certificate?: boolean
  record_update_permission: UpdatePermission
  due_rule: DueRule | null
  due_rule_note: string | null
  reminder_rule: ReminderRule | null
  target_role_note: string | null
  target_role_ids: number[]
  has_session_sequence: boolean
  is_repeatable: boolean
  lms_course_id: number | null
  signup_url: string | null
  external_provider: string | null
  cloned_from_id: number | null
  notes: string | null
  is_active: boolean
  competencies: Pick<Competency, 'id' | 'name' | 'seq'>[]
}

export interface DueRule {
  initial?: { trigger: 'hire' | 'role_change' | 'fixed' | 'immediate'; days?: number; date?: string }
  recurrence_months?: number | null
  resets_on_role_change?: boolean
}

export interface ReminderRule {
  offsets_days?: number[]
  channels?: string[]
  escalate_overdue_days?: number | null
}

export interface TrainingRecord {
  id: number
  training_id: number
  training_title: string
  training_type: TrainingType
  is_mandatory: boolean | null
  user_id: number
  training_date: string
  hours: number | null
  reported_by: 'self' | 'admin'
  source: 'manual' | 'in_app' | 'calendar' | 'import' | 'external'
  external_provider: string | null
  external_ref: string | null
  /** From the training, not the record. */
  requires_certificate?: boolean
  /** The training requires a certificate and this record has none yet.
   *  Derived server-side, so turning the requirement on retroactively
   *  surfaces every completion now short of evidence. */
  certificate_missing?: boolean
  certificate: { filename: string; url: string | null; mime: string | null; size_bytes: number | null } | null
  status: 'recorded' | 'voided'
  void_reason: string | null
  notes: string | null
  /** True when the staff member certified this completion themselves. Null means
   *  nobody was asked — an admin recorded it, or it predates the attestation. */
  attested: boolean | null
  attested_at: string | null
  created_at: string
}

export interface Assignment {
  id: number
  training_id: number
  training_title: string
  training_type: TrainingType
  is_mandatory: boolean | null
  hours: number | null
  user_id: number
  assigned_via: 'rule' | 'role' | 'individual'
  /** null = the rule could not be evaluated — almost always a missing hire date. */
  due_date: string | null
  due_basis: string | null
  due_basis_date: string | null
  status: 'pending' | 'due_soon' | 'overdue' | 'completed' | 'cancelled'
  completed_date: string | null
  notes: string | null
  /** Where to take or book this training, when it lives outside WeaveIn. */
  signup_url: string | null
  /** The person's name; only the per-training list fills it in (TRUS-562). */
  user_name?: string | null
}

/** One person who reports to the signed-in manager, and what they owe. */
export interface TeamMemberCompliance {
  user_id: number
  name: string | null
  email: string | null
  /** Counts keyed by assignment status. EMPTY means nothing is assigned to
   *  them at all — a finding for their manager, not an absence of data. */
  by_status: Record<string, number>
  earliest_due: string | null
  /** Who this person reports to. The team is the caller's whole line, not one
   *  level (TRUS-592), so this is how a direct report is told apart from
   *  someone further down. Null when their Manager field is blank or free text. */
  reports_to?: { user_id: number; name: string | null } | null
  is_direct_report?: boolean
}

export interface TeamCompliance {
  manager_user_id: number
  team: TeamMemberCompliance[]
  totals: Record<string, number>
}

export interface AttendanceImportRow {
  line: number
  email: string | null
  user_id?: number
  training_id?: number
  training_title?: string
  training_date?: string
  record_id?: number
  error?: string
}

export interface AttendanceImportResult {
  dry_run: boolean
  filename: string
  created: number
  already_recorded: number
  failed: number
  created_detail: AttendanceImportRow[]
  already_recorded_detail: AttendanceImportRow[]
  failed_detail: AttendanceImportRow[]
  message: string
}

export interface ComplianceSummary {
  by_status: Record<string, number>
  /** Assignments with no computable due date. Neither compliant nor overdue — unknown. */
  unevaluated: number
  mandatory_open: number
  overdue_staff: { user_id: number; overdue_count: number; earliest_due: string | null }[]
}

export interface ReportRow {
  record_id: number
  user_id: number
  staff_name: string | null
  email: string | null
  employee_id: string | null
  job_title: string | null
  level: string | null
  program_affiliation: string | null
  date_of_hire: string | null
  manager_name: string | null
  training_title: string
  training_type: TrainingType
  is_mandatory: boolean | null
  competencies: string
  training_date: string
  hours: number | null
  reported_by: string
  source: string
  external_provider: string | null
  certificate_filename: string | null
  status: string
}

function qs(params: Record<string, string | number | boolean | null | undefined>): string {
  const search = new URLSearchParams()
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') search.set(k, String(v))
  })
  const s = search.toString()
  return s ? `?${s}` : ''
}

// --- catalogue ------------------------------------------------------------

export function listTrainings(params: {
  training_type?: string
  modality?: string
  q?: string
  include_inactive?: boolean
  competency_id?: number
} = {}) {
  return authFetchJson<{ trainings: Training[]; count: number; total: number }>(
    getWeaveinApiUrl(`${BASE}/trainings${qs(params)}`),
  )
}

export function getTraining(id: number) {
  return authFetchJson<Training>(getWeaveinApiUrl(`${BASE}/trainings/${id}`))
}

export function createTraining(body: Record<string, unknown>) {
  return authFetchJson<Training>(getWeaveinApiUrl(`${BASE}/trainings`), { method: 'POST', jsonBody: body })
}

export function updateTraining(id: number, body: Record<string, unknown>) {
  return authFetchJson<Training>(getWeaveinApiUrl(`${BASE}/trainings/${id}`), { method: 'PATCH', jsonBody: body })
}

/** What retiring a training would do: people who still owe it (their open
 *  assignments get cancelled) and completion records (always kept). */
export interface RetirePreview {
  id: number
  open_assignments: number
  people_with_open_assignments: number
  completed_records: number
}

export function retirePreview(id: number) {
  return authFetchJson<RetirePreview>(getWeaveinApiUrl(`${BASE}/trainings/${id}/retire-preview`))
}

/** Retire a training. When people still have it assigned the server refuses (409)
 *  unless `confirmOpenAssignments` is set — show retirePreview first. */
export function retireTraining(id: number, confirmOpenAssignments = false) {
  return authFetchJson<{ id: number; cancelled_assignments: number; completed_records_kept: number }>(
    getWeaveinApiUrl(`${BASE}/trainings/${id}${qs({ confirm_open_assignments: confirmOpenAssignments || undefined })}`),
    { method: 'DELETE' },
  )
}

export function cloneTraining(id: number, title?: string) {
  return authFetchJson<Training>(getWeaveinApiUrl(`${BASE}/trainings/${id}/clone${qs({ title })}`), {
    method: 'POST',
  })
}

export function listCompetencies() {
  return authFetchJson<{ competencies: Competency[]; count: number }>(
    getWeaveinApiUrl(`${BASE}/trainings/competencies`),
  )
}

// --- completion records ---------------------------------------------------

export function listRecords(params: { user_id?: number; training_id?: number; status?: string } = {}) {
  return authFetchJson<{ records: TrainingRecord[]; count: number; total: number }>(
    getWeaveinApiUrl(`${BASE}/training-records${qs(params)}`),
  )
}

export function myRecords() {
  return authFetchJson<{
    user_id: number
    records: TrainingRecord[]
    self_reportable_trainings: { id: number; title: string; training_type: string; hours: number | null }[]
    /** False when the caller may ACT in this org but is not a member of it — a
     *  platform admin holds no user_organizations row, so a completion cannot
     *  be recorded against them here. Optional: an older API omits it, and the
     *  page treats absence as "member" so nothing is hidden from everyone. */
    is_org_member?: boolean
    /** 'not_a_member' when is_org_member is false. */
    self_report_blocked_reason?: string | null
  }>(getWeaveinApiUrl(`${BASE}/training-records/my`))
}

export function createRecords(body: {
  training_id: number
  training_date: string
  user_ids: number[]
  hours?: number | null
  source?: string
  external_provider?: string | null
  notes?: string | null
  /** Required by the server on a self-report, and stored. */
  attested?: boolean
  /** 'self' when someone reports their own completion, whatever their role (TRUS-766).
   *  Omitted, the server picks 'admin' for admins and 'self' for everyone else. */
  reported_by?: 'self' | 'admin'
}) {
  return authFetchJson<{
    created: number
    created_ids: number[]
    already_recorded_user_ids: number[]
    /** TRUS-562: one-time trainings these people had already completed; nothing was
     *  recorded for them. Optional: an older API omits it. */
    already_completed_user_ids?: number[]
  }>(getWeaveinApiUrl(`${BASE}/training-records`), { method: 'POST', jsonBody: body })
}

/**
 * TRUS-562: a repeating training that was completed and whose next cycle is not due yet.
 * The assignment stays open for the next cycle (status `pending`, next due date set), but
 * the person is done for now, so screens show it as completed with its next due date
 * instead of under Outstanding.
 */
export function isDoneUntilNextDue(a: Pick<Assignment, 'status' | 'completed_date'>): boolean {
  return a.status === 'pending' && Boolean(a.completed_date)
}

/** The org's LMS courses, for linking a training to in-app content (TRUS-549 ask 2).
 *  `staff_trainings.lms_course_id` has existed since migration 701 with nothing to set
 *  it; this is what fills the picker. Drafts are included and labelled, because linking
 *  a training to a course that is still being written is a normal thing to do. */
export function listLmsCourses(cboId: number) {
  return authFetchJson<{ id: number; title: string; is_published: boolean }[]>(
    getWeaveinApiUrl(`/api/lms/admin/courses?cbo_id=${cboId}`),
  )
}

export function voidRecord(id: number, reason: string) {
  return authFetchJson<TrainingRecord>(getWeaveinApiUrl(`${BASE}/training-records/${id}/void`), {
    method: 'POST',
    jsonBody: { reason },
  })
}

/** Certificate upload is multipart, so it bypasses the JSON helper. */
export async function uploadCertificate(recordId: number, file: File) {
  const form = new FormData()
  form.append('file', file)
  const res = await authFetch(getWeaveinApiUrl(`${BASE}/training-records/${recordId}/certificate`), {
    method: 'POST',
    body: form,
  })
  if (!res.ok) {
    let detail = 'Upload failed'
    try {
      detail = (await res.json())?.detail || detail
    } catch {
      /* keep the generic message */
    }
    throw new Error(detail)
  }
  return res.json() as Promise<TrainingRecord>
}

// --- assignments ----------------------------------------------------------

export function listAssignments(
  params: {
    user_id?: number
    training_id?: number
    status?: string
    open_only?: boolean
    include_cancelled?: boolean
    limit?: number
  } = {},
) {
  return authFetchJson<{ assignments: Assignment[]; count: number; total: number }>(
    getWeaveinApiUrl(`${BASE}/training-assignments${qs(params)}`),
  )
}

/** Take a training off a person (TRUS-562). Admins, or a manager above the person in
 *  the reporting line. The nightly evaluator leaves them off afterwards; assigning
 *  them again by hand puts it back. Completion records are not touched. */
export function unassignTraining(assignmentId: number, reason?: string) {
  return authFetchJson<{ id: number; status: 'cancelled' }>(
    getWeaveinApiUrl(`${BASE}/training-assignments/${assignmentId}/cancel`),
    { method: 'POST', jsonBody: { reason: reason?.trim() || null } },
  )
}

export function myAssignments() {
  return authFetchJson<{ user_id: number; assignments: Assignment[]; overdue: number; due_soon: number }>(
    getWeaveinApiUrl(`${BASE}/training-assignments/my`),
  )
}

export function complianceSummary() {
  return authFetchJson<ComplianceSummary>(getWeaveinApiUrl(`${BASE}/training-assignments/summary`))
}

export function assignTraining(body: {
  training_id: number
  user_ids?: number[]
  role_ids?: number[]
  due_date?: string | null
  notes?: string | null
}) {
  return authFetchJson<{ assigned: number; assigned_user_ids: number[]; already_assigned_user_ids: number[] }>(
    getWeaveinApiUrl(`${BASE}/training-assignments`),
    { method: 'POST', jsonBody: body },
  )
}

/**
 * Who an assignment can target.
 *
 * Two independent pickers, because the backend takes two independent lists and
 * means different things by them: `user_ids` is "these people", `role_ids` is
 * "whoever holds this workforce role, now" — the latter resolved server-side
 * through workforce_role_label_map, so a role assignment reaches staff the
 * admin never enumerated.
 */
export interface AssignableStaff {
  id: number
  name: string
  email: string
  user_org_role: string | null
  role_names?: string | null
  last_login: string | null
}

export interface WorkforceRole {
  id: number
  role_name: string
  role_code?: string | null
  is_active?: boolean
}

/**
 * Staff only. Without `staff_only` the org user list also returns portal
 * members and synthetic rows for pending invitations (negative ids), neither
 * of which can hold a training assignment.
 */
export function listAssignableStaff(cboId: number) {
  return authFetchJson<AssignableStaff[]>(
    getWeaveinApiUrl(`/api/cbos/${cboId}/users?staff_only=true&exclude_internal=true`),
  )
}

export function listWorkforceRoles() {
  return authFetchJson<{ items: WorkforceRole[]; total: number }>(
    getWeaveinApiUrl(`${BASE}/roles?active_only=true`),
  )
}

/** What the people reporting to me owe. The set is derived from the reporting
 *  line server-side — there is no user filter to pass, which is why this is
 *  readable by a manager and not only an admin. */
export function myTeamCompliance() {
  return authFetchJson<TeamCompliance>(getWeaveinApiUrl(`${BASE}/training-assignments/my-team`))
}

/** One person below the signed-in manager and their completion records. */
export interface TeamMemberRecords {
  user_id: number
  name: string | null
  email: string | null
  records: TrainingRecord[]
  record_count: number
}

export interface TeamRecords {
  manager_user_id: number
  reports: TeamMemberRecords[]
}

/** The completion records of everyone below me in the reporting line — directly
 *  and down the chain (TRUS-592). Like myTeamCompliance there is no user filter:
 *  the server derives the set. It also needs the `training.view_team_records`
 *  function on the caller's role (admins skip that), so a 403 here means "not
 *  granted yet", not an error in the page. */
export function myTeamRecords() {
  return authFetchJson<TeamRecords>(getWeaveinApiUrl(`${BASE}/training-records/my-team`))
}

/** Everything about one person's training, for their employee record (TRUS-562).
 *  Readable by an admin, the person, or anyone above them in the reporting line. */
export interface PersonTraining {
  user_id: number
  assignments: Assignment[]
  counts: Record<string, number>
  /** null when the caller may see assignments but not completion records — a
   *  manager whose role lacks `training.view_team_records`. */
  records: TrainingRecord[] | null
  records_restricted: boolean
  /** The caller may unassign this person's trainings (admin, or above them in the line). */
  can_unassign?: boolean
}

export function personTraining(userId: number) {
  return authFetchJson<PersonTraining>(getWeaveinApiUrl(`${BASE}/training-records/person/${userId}`))
}

export interface StaffProfileField {
  id: number
  name: string
  friendly_name: string | null
  type: string
  value: unknown
}

export interface StaffProfile {
  user: { id: number; name: string | null; email: string | null; phone: string | null; role: string | null }
  groups: { category: string; label: string; fields: StaffProfileField[] }[]
}

/** A staff member's profile. The server allows it for a profile admin, the person
 *  themselves, or anyone above them in the reporting line (TRUS-592). */
export function getStaffProfile(userId: number) {
  return authFetchJson<StaffProfile>(getWeaveinApiUrl(`/api/staff-profiles/${userId}`))
}

/**
 * Record attendance from an uploaded register.
 *
 * ALWAYS dry-run first and show the result. The server defaults dry_run to
 * true and executes every row for real before rolling back, so the preview is
 * an outcome rather than a prediction — and these rows are evidence under a
 * retention obligation, so an admin should see the whole result before any of
 * it is kept.
 */
export async function importAttendance(file: File, dryRun: boolean) {
  const form = new FormData()
  form.append('file', file)
  form.append('dry_run', String(dryRun))
  const res = await authFetch(getWeaveinApiUrl(`${BASE}/training-records/import`), {
    method: 'POST',
    body: form,
  })
  if (!res.ok) {
    const detail = await res.json().catch(() => null)
    throw new Error(detail?.detail || `Import failed (${res.status})`)
  }
  return (await res.json()) as AttendanceImportResult
}

export function runEvaluator() {
  return authFetchJson<Record<string, number>>(getWeaveinApiUrl(`${BASE}/training-assignments/evaluate`), {
    method: 'POST',
  })
}

// --- reporting ------------------------------------------------------------

export function trainingReport(params: Record<string, string | number | boolean | undefined> = {}) {
  return authFetchJson<{ rows: ReportRow[]; count: number; columns: string[] }>(
    getWeaveinApiUrl(`${BASE}/training-reports/records${qs(params)}`),
  )
}

export function competencyCoverage() {
  return authFetchJson<{
    competencies: { id: number; seq: number | null; name: string; staff_completed: number; trainings_mapped: number }[]
    unmapped_trainings: number
  }>(getWeaveinApiUrl(`${BASE}/training-reports/competency-coverage`))
}

/**
 * Export downloads through authFetch and a blob, never a bare href — the endpoint needs
 * the Authorization and X-CBO-ID headers, and a plain link sends neither (that mistake
 * has produced 401s on other authed downloads in this app).
 */
export async function downloadReport(fmt: 'csv' | 'xlsx', params: Record<string, string | number | boolean | undefined>) {
  const res = await authFetch(getWeaveinApiUrl(`${BASE}/training-reports/records/export${qs({ ...params, fmt })}`))
  if (!res.ok) {
    let detail = 'Export failed'
    try {
      detail = (await res.json())?.detail || detail
    } catch {
      /* keep the generic message */
    }
    throw new Error(detail)
  }
  const blob = await res.blob()
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `training_records.${fmt}`
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

// --- shared formatting ----------------------------------------------------

export function formatDate(value: string | null | undefined): string {
  if (!value) return '—'
  const d = new Date(`${value}T00:00:00`)
  if (Number.isNaN(d.getTime())) return String(value)
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

/** "Mandatory" / "Optional" / "Not set" — never render an undecided flag as "No". */
export function mandatoryLabel(value: boolean | null): string {
  if (value === null || value === undefined) return 'Not set'
  return value ? 'Mandatory' : 'Optional'
}

export function statusTone(status: Assignment['status']): 'neutral' | 'success' | 'attention' | 'danger' {
  if (status === 'overdue') return 'danger'
  if (status === 'due_soon') return 'attention'
  if (status === 'completed') return 'success'
  return 'neutral'
}
